import React from "react";
import {
  Datepicker as BaseUIDatepicker,
  DatepickerProps as BaseUIDatepickerProps,
  DatepickerOverrides,
} from "baseui/datepicker";
import { mergeOverrides } from "baseui";
import { Overrides } from "baseui/overrides";
import { PLACEMENT } from "baseui/popover";
import styled from "styled-components";
import { ChevronSortDown, ChevronLeft, ChevronRight } from "@carbon/icons-react";
import { Theme } from "../../../../styling/baseui_theme";

const getOverrides = (
  range: boolean,
  dataTest: string,
  disableMonthYear: boolean,
): Overrides<
  DatepickerOverrides & {
    $selected: boolean;
    $isHovered: boolean;
    $isHighlighted: boolean;
    $error: boolean;
    disableMonthYear: boolean;
  }
> => {
  return {
    Popover: {
      props: {
        placement: PLACEMENT.bottomLeft,
        overrides: {
          Body: {
            style: ({ $theme }: { $theme: Theme }) => ({
              zIndex: $theme.zIndex.modal,
            }),
          },
        },
      },
    },
    InputWrapper: {
      style: () => ({
        width: range ? "250px" : "121px",
      }),
    },
    MonthYearSelectButton: {
      props: {
        "data-test": disableMonthYear ? `${dataTest}-input` : `${dataTest}-month-year-input`,
        disabled: disableMonthYear,
      },
      style: ({ $theme }: { $theme: Theme }) => ({
        fontSize: $theme.typography.LabelMedium.fontSize,
        fontWeight: $theme.typography.LabelMedium.fontWeight,
        lineHeight: $theme.typography.LabelMedium.lineHeight,
        cursor: disableMonthYear ? "default" : "",
      }),
    },
    MonthHeader: {
      style: ({ $theme }: { $theme: Theme }) => ({
        color: $theme.colors.contentTertiary,
      }),
    },
    MonthYearSelectIconContainer: {
      component: () =>
        disableMonthYear ? null : (
          <StyledChevronDown data-test={`${dataTest}-month-year-icon`}>
            <ChevronSortDown size={20} />
          </StyledChevronDown>
        ),
    },
    MonthYearSelectStatefulMenu: {
      props: {
        overrides: {
          List: {
            style: {
              borderTopLeftRadius: "0px",
              borderTopRightRadius: "0px",
              borderBottomLeftRadius: "0px",
              borderBottomRightRadius: "0px",
            },
          },
        },
      },
    },
    PrevButtonIcon: {
      component: () => (
        <StyledMonthButton data-test={`${dataTest}-prev-month`}>
          <ChevronLeft size={20} />
        </StyledMonthButton>
      ),
    },
    NextButtonIcon: {
      component: () => (
        <StyledMonthButton data-test={`${dataTest}-next-month`}>
          <ChevronRight size={20} />
        </StyledMonthButton>
      ),
    },
    Day: {
      style: ({
        $theme,
        $selected,
        $isHovered,
        $isHighlighted,
      }: {
        $theme: Theme;
        $selected: boolean;
        $isHovered: boolean;
        $isHighlighted: boolean;
      }) => ({
        ":before": {
          backgroundColor: $theme.colors.backgroundTertiary,
        },
        ":after": {
          backgroundColor: $selected
            ? $theme.colors.backgroundInverseSecondary
            : $isHovered || $isHighlighted
              ? $theme.colors.backgroundTertiary
              : $theme.colors.white,
        },
      }),
      props: {
        "data-test": `${dataTest}-day`,
      },
    },
    Input: {
      props: {
        overrides: {
          Input: {
            props: {
              "data-test": `${dataTest}-input`,
            },
            style: ({ $theme }: { $theme: Theme }) => ({
              fontSize: $theme.typography.font200.fontSize,
              fontWeight: $theme.typography.font200.fontWeight,
              lineHeight: $theme.typography.font200.lineHeight,
            }),
          },
          InputContainer: {
            style: ({ $theme, $error }: { $theme: Theme; $error: boolean }) => ({
              ...(!$error && { backgroundColor: $theme.colors.backgroundAlwaysLight }),
            }),
          },
          Root: {
            style: ({
              $theme,
              $isFocused,
              $disabled,
              $error,
            }: {
              $theme: Theme;
              $isFocused: boolean;
              $disabled: boolean;
              $error: boolean;
            }) => ({
              ...(!$error && {
                ...(!$isFocused && {
                  borderTopColor: $theme.colors.mono600,
                  borderBottomColor: $theme.colors.mono600,
                  borderLeftColor: $theme.colors.mono600,
                  borderRightColor: $theme.colors.mono600,
                }),
                ...($disabled && {
                  borderTopColor: $theme.colors.mono400,
                  borderBottomColor: $theme.colors.mono400,
                  borderLeftColor: $theme.colors.mono400,
                  borderRightColor: $theme.colors.mono400,
                }),
              }),
              borderTopLeftRadius: "0px",
              borderTopRightRadius: "0px",
              borderBottomLeftRadius: "0px",
              borderBottomRightRadius: "0px",
            }),
          },
        },
      },
    },
  };
};

const StyledMonthButton = styled.div`
  color: ${({ theme }) => theme.colors.contentPrimary};
`;

const StyledChevronDown = styled.div`
  margin-left: ${({ theme }) => theme.sizing.scale500};
  margin-bottom: ${({ theme }) => theme.sizing.scale100};
`;

export type DeprecatedDatepickerProps = Omit<
  BaseUIDatepickerProps,
  | "autoFocusCalendar"
  | "clearable"
  | "dateLabel"
  | "excludeDates"
  | "filterDate"
  | "fixedHeight"
  | "formatString"
  | "highlightDate"
  | "includesDates"
  | "locale"
  | "monthsShown"
  | "mountNode"
  | "onClose"
  | "onDayClick"
  | "onDayMouseLeave"
  | "onDayMouseOver"
  | "onMonthChange"
  | "onYearChange"
  | "orientation"
  | "peekNextMonth"
  | "positive"
  | "quickSelect" // named in designs as LookbackTime; cut from scope
  | "quickSelectOptions"
  | "timeSelectEnd"
  | "timeSelectStart"
  | "trapTabbing"
> & {
  // requiring inputAriaLabel because labels should be unique for full a11y compliance,
  // therefore we shouldn't default to a label, which makes non-uniquenes easy
  inputAriaLabel: string;
  formatString?: string;
  styletronOverrides?: DatepickerOverrides;
  inputBackgroundAlwaysLight?: boolean;
  dataTest?: string;
};

export const DeprecatedDatepicker = ({
  size,
  value,
  onChange,
  minDate,
  maxDate,
  mask,
  placeholder,
  error,
  range = false,
  displayValueAtRangeIndex,
  disabled = false,
  inputAriaLabel,
  formatString,
  styletronOverrides,
  dataTest = "datepicker-input",
}: DeprecatedDatepickerProps) => {
  // There is a bug in the month/year selector when a minDate and maxDate are
  // being used - sometimes user will be stuck unable to select a new month and
  // year (for example if the minDate and maxDate are set at 3/1/21 and 5/1/22
  // and then user selects 1/1/22, they will no longer be able to select anything
  // 6/21 - 12/21). This boolean will disable the month and year dropdowns when
  // minDate and maxDate are set (until this bug can be resolved).
  const disableMonthYear = minDate !== undefined || maxDate !== undefined;

  let styleOverride = getOverrides(range, dataTest, disableMonthYear);

  if (styletronOverrides) {
    styleOverride = mergeOverrides(
      styleOverride,
      styletronOverrides as Overrides<
        DatepickerOverrides & {
          $selected: boolean;
          $isHovered: boolean;
          $isHighlighted: boolean;
        }
      >,
    );
  }
  return (
    <BaseUIDatepicker
      size={size}
      aria-label={inputAriaLabel}
      aria-describedby={`datepicker--screenreader--message--${inputAriaLabel.replace(/\s/g, "-")}`}
      value={value}
      onChange={onChange}
      minDate={minDate}
      maxDate={maxDate}
      mask={mask}
      placeholder={placeholder}
      error={error}
      range={range}
      displayValueAtRangeIndex={displayValueAtRangeIndex}
      disabled={disabled}
      formatString={formatString}
      overrides={mergeOverrides(
        styleOverride,
        styletronOverrides as
          | Overrides<
              DatepickerOverrides & {
                $selected: boolean;
                $isHovered: boolean;
                $isHighlighted: boolean;
                $error: boolean;
              }
            >
          | undefined,
      )}
    />
  );
};
